import { OBSERVATION_BG_IMAGE } from "@/lib/fieldNotes";
import LeafSignature from "@/components/heritage/LeafSignature";

interface IslandObservationProps {
  quote?: string;
  attribution?: string;
  image?: string | null;
  tone?: "dark" | "light";
  className?: string;
}

/**
 * Reusable cinematic editorial pull-quote block.
 * No stars, no testimonial styling — pure magazine.
 */
const IslandObservation = ({
  quote = "What stayed with me most was how mentally quiet the islands felt. Not boring. Quiet.",
  attribution = "Visitor Field Note",
  image = OBSERVATION_BG_IMAGE,
  tone = "dark",
  className = "",
}: IslandObservationProps) => {
  const isDark = tone === "dark";
  return (
    <section
      className={`relative overflow-hidden ${className}`}
      aria-label="Island observation"
    >
      <div className="relative min-h-[44vh] md:min-h-[56vh] flex items-center justify-center">
        {/* Seamless top & bottom fades into surrounding sections */}
        <div
          aria-hidden
          className="absolute top-0 inset-x-0 h-24 md:h-32 z-[5] pointer-events-none"
          style={{
            background: isDark
              ? "linear-gradient(180deg, hsl(var(--background)) 0%, transparent 100%)"
              : "linear-gradient(180deg, hsl(var(--background)) 0%, transparent 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute bottom-0 inset-x-0 h-24 md:h-32 z-[5] pointer-events-none"
          style={{
            background: isDark
              ? "linear-gradient(180deg, transparent 0%, hsl(var(--background)) 100%)"
              : "linear-gradient(180deg, transparent 0%, hsl(var(--background)) 100%)",
          }}
        />
        {image && (
          <img
            src={image}
            alt=""
            aria-hidden
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover scale-105 animate-[fade-in_1.6s_ease-out]"
            style={{
              animation: "fade-in 1.6s ease-out, drift 28s ease-in-out infinite alternate",
            }}
          />
        )}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background: isDark
              ? "linear-gradient(180deg, hsl(150 35% 8% / 0.7) 0%, hsl(150 35% 8% / 0.85) 60%, hsl(150 35% 8% / 0.7) 100%)"
              : "linear-gradient(180deg, hsl(40 33% 96% / 0.82) 0%, hsl(40 33% 96% / 0.92) 60%, hsl(40 33% 96% / 0.82) 100%)",
          }}
        />
        <LeafSignature
          variant="watermark"
          position={isDark ? "top-right" : "top-right"}
          size="xl"
          tone={isDark ? "dark" : "light"}
        />
        <div
          className="relative z-10 container mx-auto px-6 py-14 md:py-20 text-center"
          style={{ maxWidth: "1100px" }}
        >
          <div
            aria-hidden
            className={`mx-auto mb-8 h-px w-16 ${
              isDark ? "bg-accent/60" : "bg-[hsl(150_30%_18%)]/30"
            }`}
          />
          <blockquote
            className={`font-serif italic leading-[1.15] tracking-[-0.005em] text-[clamp(1.75rem,5vw,3.75rem)] ${
              isDark ? "text-[hsl(40_33%_96%)]" : "text-[hsl(150_30%_14%)]"
            }`}
          >
            <span className="block">&ldquo;{quote}&rdquo;</span>
          </blockquote>
          {attribution && (
            <p
              className={`mt-10 font-sans text-[10px] md:text-[11px] uppercase tracking-[0.42em] ${
                isDark ? "text-accent" : "text-[hsl(150_30%_18%)]/70"
              }`}
            >
              — {attribution}
            </p>
          )}
        </div>
      </div>
      <style>{`
        @keyframes drift {
          0% { transform: scale(1.05) translate3d(0,0,0); }
          100% { transform: scale(1.12) translate3d(-1.5%, -1%, 0); }
        }
      `}</style>
    </section>
  );
};

export default IslandObservation;
